import { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { updateProfileImage } from "../services/profileService";
import { getUserDetails } from "@redux/actions/utility-actions";
import { sessionExpireTimeout } from "@redux/actions/utility-actions";

/**
 * Custom hook for profile image management
 * Handles image upload, crop, delete, and validation
 * @param {Object} userInfo - User details object with Id, Email, Photo__c
 * @returns {Object} Image state and handlers
 */
export const useProfileImage = (userInfo) => {
  const dispatch = useDispatch();
  const [imgCrop, setImgCrop] = useState("");
  const [storeImage, setStoreImage] = useState(userInfo?.Photo__c || "");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [validationError, setValidationError] = useState("");

  // Update stored image when user info changes
  useEffect(() => {
    setStoreImage(userInfo?.Photo__c || "");
    setIsSuccess(false);
  }, [userInfo?.Photo__c]);

  // Handle image crop
  const handleCrop = useCallback((view) => {
    setImgCrop(view);
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    setValidationError("");
    setImgCrop(null);
  }, []);

  // Validate file size before upload (max 5MB)
  const handleBeforeFileLoad = useCallback((file) => {
    const fileSize = file?.nativeEvent?.srcElement?.files[0]?.size;
    if (fileSize && fileSize > 5242880) {
      setValidationError("Please upload the file whose size is less than 5MB");
    } else {
      setValidationError("");
    }
  }, []);

  // Save cropped image
  const handleSaveImage = useCallback(async () => {
    if (validationError) return;

    const base64Image = imgCrop?.split(",")?.[1];
    if (!base64Image) return;

    setStoreImage(base64Image);
    setIsModalOpen(false);
    setImgCrop("");
    setIsUploading(true);

    // Upload image to server
    const response = await updateProfileImage(
      userInfo?.Id,
      userInfo?.Email,
      base64Image
    );

    if (response.success) {
      dispatch(getUserDetails({ username: userInfo?.Email }));
      setSuccessMessage("Uploaded Successfully");
      setIsSuccess(true);
    } else {
      if (response.isUnauthorized) {
        sessionExpireTimeout(dispatch);
      }
      setStoreImage(userInfo?.Photo__c || "");
    }

    setIsUploading(false);
  }, [validationError, imgCrop, userInfo, dispatch]);

  // Remove profile image
  const handleRemoveImage = useCallback(async () => {
    setIsUploading(true);
    setStoreImage(" ");

    const response = await updateProfileImage(userInfo?.Id, userInfo?.Email, " ");

    if (response.success) {
      dispatch(getUserDetails({ username: userInfo?.Email }));
      setSuccessMessage("Removed Successfully");
      setIsSuccess(true);
    } else {
      if (response.isUnauthorized) {
        sessionExpireTimeout(dispatch);
      }
      setStoreImage(userInfo?.Photo__c || "");
    }

    setIsUploading(false);
  }, [userInfo, dispatch]);

  // Open modal
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  return {
    imgCrop,
    storeImage,
    isModalOpen,
    isUploading,
    isSuccess,
    successMessage,
    validationError,
    setIsModalOpen,
    setIsSuccess,
    handleCrop,
    handleClose,
    handleBeforeFileLoad,
    handleSaveImage,
    handleRemoveImage,
    handleOpenModal,
  };
};
